import type { MatchState, PlayerState } from './state';
import type { PlayerInput, ActionKind } from '../types';
import type { SimConfig } from '../config/simConfig';
import type { SimEvent } from './events';
import type { ContactEvent } from '../physics/collide';
import type { KickRequest } from './ballActions';
import { restartKickRefused } from './ballActions';
import { BTN } from './input';
import { canGkDive, canGkPickup, gkPickup, gkRole, inOwnBox } from './gk';
import { passChargeFor } from './passing';
import { strikeEnvelope, aerialContact, simOwnsAerialArm } from './aerial';
import { PITCH } from '../config/pitchConfig';

/** D44: distance the keeper's distribution should carry — the own teammate whose bearing best matches the
 *  aim (within a cone), else a long-clearance fallback. Keeps GK_HOLD throws calibrated to arrive with pace
 *  (single source of truth with the outfield passes; benefits human keepers too). */
function distributeDistance(state: MatchState, gk: PlayerState, aimYaw: number): number {
  let best = -1;
  let bestErr = 0.6; // cone half-width (rad) — outside this, treat as an aimed-into-space clearance
  for (const t of state.players) {
    if (t.team !== gk.team || t.id === gk.id || t.sentOff) continue;
    const bearing = Math.atan2(t.pos.y - gk.pos.y, t.pos.x - gk.pos.x);
    let err = Math.abs(((bearing - aimYaw + Math.PI) % (Math.PI * 2)) - Math.PI);
    if (err > Math.PI) err = Math.PI * 2 - err;
    if (err < bestErr) {
      bestErr = err;
      best = Math.hypot(t.pos.x - gk.pos.x, t.pos.y - gk.pos.y);
    }
  }
  return best > 0 ? best : 30; // no teammate on the aim ⇒ a firm upfield clearance
}

/** Actions whose "Inputs ignored" column is yes (§5.4) — the player does not steer while in them. */
const INPUT_IGNORED_ACTIONS: ReadonlySet<ActionKind> = new Set<ActionKind>([
  'KICK_RELEASE',
  'TACKLE_STAND',
  'TACKLE_SLIDE',
  'SLIDE_RECOVERY',
  'SHOVE',
  'PUNCH_WINDUP',
  'PUNCH_ACTIVE',
  'PUNCH_RECOVERY',
  'DIVE_FALL',
  'PRONE',
  'GETUP',
  'RAGDOLL',
  'GK_DIVE',
  'GK_SMOTHER',
]);

export function isInputIgnored(action: ActionKind): boolean {
  return INPUT_IGNORED_ACTIONS.has(action);
}

function updateSprint(p: PlayerState, buttons: number, moving: boolean, cfg: SimConfig): void {
  const mv = cfg.movement;
  const sprintHeld = (buttons & BTN.SPRINT) !== 0;
  if (p.sprinting) {
    if (!sprintHeld || !moving || p.stamina < mv.SPRINT_MIN) p.sprinting = false;
  } else if (sprintHeld && moving && p.stamina >= mv.SPRINT_REARM) {
    p.sprinting = true;
  }
}

/** D62: the kick bits the one-touch intent latch tracks. */
const KICK_BITS_MASK = BTN.SHOOT | BTN.THROUGH | BTN.PASS;

/**
 * D113 (HB-D) — contact quality, deterministically (HB-P10).
 *
 * `off01` is how far off the sweet spot the contact was, 0 (perfect) … 1 (edge of reach). The deviation
 * it buys is signed by the geometry — which SIDE of the aim the ball sat on — so a mistimed header slices
 * the way a real one would, and does it identically on every replay. No rng: goldens stay exact.
 */
function qualityDeviation(off01: number, side: number, cfg: SimConfig): number {
  const q = off01 < 0 ? 0 : off01 > 1 ? 1 : off01;
  return q * cfg.actions.aerial.QUALITY_MAX_DEV * (side < 0 ? -1 : 1);
}

/**
 * D135 (§5.2, ✅ user: "pressing shot while in-flight auto-aims at the goal") — the strike yaw.
 *
 * The player's own aim is the FALLBACK, not the input: at the moment of an aerial contact you are
 * watching the ball, not the frame, and a volley whose direction is whatever the camera happened to face
 * is the least readable outcome in the game. The strike therefore solves toward the goal — bounded by
 * `dev`, the deflection-quality cone the contact geometry already produced, so a scuffed volley can still
 * miss but the INTENT is always goalward. Applies to volley and header, humans and bots alike: the sim is
 * input-only and has no idea which is which.
 */
function strikeYaw(state: MatchState, p: PlayerState, aimYaw: number, dev: number, cfg: SimConfig): number {
  if (cfg.actions.aerial.STRIKE_GOAL_AIM !== 1) return aimYaw + dev;
  const pitch = PITCH[state.mode.teamSize];
  const goalX = (state.attackDir[p.team] * pitch.length) / 2;
  const toGoal = Math.atan2(-p.pos.y * 0.35, goalX - p.pos.x); // at the frame, biased to the middle
  return toGoal + dev;
}

/** Signed lateral offset of the ball from the strike aim, in metres (+ = left of the aim ray). */
function lateralOffset(p: PlayerState, ball: MatchState['ball'], aimYaw: number): number {
  const dx = ball.pos.x - p.pos.x;
  const dy = ball.pos.y - p.pos.y;
  return -Math.sin(aimYaw) * dx + Math.cos(aimYaw) * dy;
}

/**
 * D113 — the aerial strike, the receiver-side half of the high-ball wave (HB-P2).
 *
 * An ARMED SHOOT (the same D62 intent latch the ground one-touch uses — no new button) over a free ball
 * in the air becomes a first-time strike. Two verbs by height: a VOLLEY in the shin-to-chest band, struck
 * on the spot; a HEADER above it, which commits the player to `HEADER_JUMP` and resolves on contact
 * during the IP:406 window. The jump also fires when the ball is not YET in the header band but will
 * drop into it within the window — otherwise you could never head a ball that is still above your head
 * when you have to leave the ground.
 *
 * Returns 'STRUCK' or 'WAITING' when it took the tick (the caller must not also run the ordinary kick
 * machinery), 'NONE' when this is not an aerial situation at all. D155: WAITING is a declared finish
 * whose contact has not arrived — see the VOLLEY branch.
 */
type AerialOutcome = 'STRUCK' | 'WAITING' | 'NONE';

function tryAerialStrike(state: MatchState, p: PlayerState, input: PlayerInput | undefined, buttons: number, cfg: SimConfig, kicks: KickRequest[], claim: { struck: boolean }): AerialOutcome {
  if (!input) return 'NONE';
  const A = cfg.actions.aerial;
  const b = state.ball;
  if (b.possessor !== null) return 'NONE'; // a carried ball is nobody's volley
  // HB-P5: one ball, one strike per tick. The player loop runs in ascending EntityId, so a contested
  // aerial goes to the lower id — the house tie-break, and normative by iteration order alone. Without
  // this, two armed strikers both queue a kick and the LAST one silently overwrites the first.
  if (claim.struck) return 'NONE';
  // ARMED. Arming is the D62 press edge, so merely holding SHOOT through your own last kick can never
  // strike — the same rule that keeps the ground one-touch honest. D135/PT4-P11: the button no longer has
  // to be DOWN at contact; `armAerialIntent` keeps a declared finish alive while the ball is finishable,
  // and clears it otherwise, so "still held" would be a second, weaker copy of that rule.
  if ((p.intentBits & BTN.SHOOT) === 0) return 'NONE';
  if (cfg.actions.aerial.TAP_ARM !== 1 && (buttons & BTN.SHOOT) === 0) return 'NONE';
  // ONE envelope test, shared with the client's header-ready cue — the reticle can never light up for a
  // strike the sim then refuses, nor stay dark on one it would have given.
  const kind = strikeEnvelope(b, p.pos, cfg, state.conditions);
  if (kind === null) return 'NONE';

  const aimYaw = input.aimYaw;
  if (kind === 'VOLLEY') {
    // D155 — the lookahead says the ball IS finishable; it does not say to hit it yet. `strikeEnvelope`
    // answers VOLLEY for a contact up to VOLLEY_WINDOW (3) ticks away, and striking on that verdict
    // queued a kick that `applyKicks` then refused: it runs at sub-step 4b, AFTER the ball integrates at
    // step 4, and drops any kick whose ball is beyond kick.REACH. The declaration was consumed anyway
    // and the striker parked in KICK_RELEASE for six ticks, so the ball he had called simply bounced off
    // him — strictly worse than the tunnelling D135 replaced.
    //
    // A volley has no wind-up to commit to (that is what separates it from HEADER_JUMP, which must leave
    // the ground early and therefore keeps its lookahead), so the honest gate is "is the contact the one
    // applyKicks will measure?". Tick 1 IS that position: the ball's post-integration point. Anything
    // farther out, we return false and leave the arm intact — PT4-P11's sticky arm holds the declaration
    // while the ball hangs, so the countdown 3 → 2 → 1 simply strikes on the tick it can land.
    const contact = aerialContact(b, p.pos, cfg, state.conditions, A.VOLLEY_Z, A.STRIKE_REACH, A.VOLLEY_WINDOW);
    if (contact !== null && contact.tick > 1) return 'WAITING';
    // VOLLEY — a first-time strike that never needed a claim (above CLAIM_Z the ball is dead weight to
    // the possession system, so before D113 this ball was simply unplayable).
    const lat = lateralOffset(p, b, aimYaw);
    const dev = qualityDeviation(Math.abs(lat) / A.STRIKE_REACH, lat, cfg);
    const incoming = Math.hypot(b.vel.x, b.vel.y, b.vel.z);
    kicks.push({
      entity: p.id,
      team: p.team,
      button: BTN.SHOOT,
      chargeTicks: cfg.actions.kick.CHARGE_CAP,
      aimYaw: strikeYaw(state, p, aimYaw, dev, cfg), // D135: goalward, bounded by the contact quality
      aimElev: input.aimElev,
      assist: false, // a strike is never soft-locked to a receiver
      speed: A.VOLLEY_SPEED + A.REDIRECT_KEEP * incoming,
      elev: A.VOLLEY_ELEV + Math.max(0, Math.min(1, input.aimElev)) * cfg.actions.kick.ELEV_GAIN,
    });
    p.intentBits &= ~BTN.SHOOT; // consumed
    claim.struck = true;
    p.action = 'KICK_RELEASE';
    p.actionTicks = 0;
    p.charge = null;
    return 'STRUCK';
  }
  // HEADER — in the band now, or dropping into it within the jump window
  p.action = 'HEADER_JUMP';
  p.actionTicks = 0;
  p.charge = null;
  p.vz = A.HEADER_JUMP_VZ; // IP:406 — flight 14 t, apex tick 7. movement.ts integrates it from here.
  return 'STRUCK';
}

/**
 * D113 — the head-redirect, during the IP:406 effect window. The first tick the ball is inside the head
 * sphere it is struck; the intent is consumed, so a jump yields exactly one contact. Missing the window
 * is the whiff, and the airtime is its whole cost.
 */
function resolveHeaderContact(state: MatchState, p: PlayerState, input: PlayerInput | undefined, cfg: SimConfig, kicks: KickRequest[], claim: { struck: boolean }): void {
  const A = cfg.actions.aerial;
  if (claim.struck) return; // HB-P5: one strike per ball per tick, ascending EntityId
  if (!input || (p.intentBits & BTN.SHOOT) === 0) return;
  if (p.actionTicks < A.HEADER_WINDOW[0] || p.actionTicks > A.HEADER_WINDOW[1]) return;
  const b = state.ball;
  if (b.possessor !== null) return;
  if (Math.hypot(b.pos.x - p.pos.x, b.pos.y - p.pos.y) > A.STRIKE_REACH) return;
  const headZ = p.z + A.HEAD_Z;
  if (Math.abs(b.pos.z - headZ) > A.STRIKE_REACH) return;

  const aimYaw = input.aimYaw;
  const lat = lateralOffset(p, b, aimYaw);
  // two independent quality terms, both deterministic: WHERE on the head it hit, and WHEN in the window.
  const mid = (A.HEADER_WINDOW[0] + A.HEADER_WINDOW[1]) / 2;
  const half = Math.max(1e-6, (A.HEADER_WINDOW[1] - A.HEADER_WINDOW[0]) / 2);
  const timing = (p.actionTicks - mid) / half; // −1 early … +1 late
  const dev = qualityDeviation(Math.abs(lat) / A.STRIKE_REACH, lat, cfg);
  const incoming = Math.hypot(b.vel.x, b.vel.y, b.vel.z);
  kicks.push({
    entity: p.id,
    team: p.team,
    button: BTN.SHOOT,
    chargeTicks: cfg.actions.kick.CHARGE_CAP,
    aimYaw: strikeYaw(state, p, aimYaw, dev, cfg), // D135
    aimElev: input.aimElev,
    assist: false,
    speed: A.HEADER_SPEED + A.REDIRECT_KEEP * incoming,
    // met late (on the way down off your own apex) the ball balloons; met early it is headed down.
    elev: cfg.actions.kick.LOFT.SHOOT + timing * A.QUALITY_MAX_DEV + Math.max(0, Math.min(1, input.aimElev)) * cfg.actions.kick.ELEV_GAIN,
  });
  p.intentBits &= ~BTN.SHOOT; // consumed — one contact per jump
  claim.struck = true;
}

/**
 * D135 (§5.2) — the ONE arming rule, run for every player in every state, before the action machine.
 *
 * · a kick PRESSED while the ball is not yet mine is a DECLARED first-time finish (the D62 latch: never
 *   a raw held bit, or the very press that launched a pass would fire again the instant it came back);
 * · while a free ball is AIRBORNE and finishable the arm does not expire (D113 — a cross hangs 40+ ticks,
 *   far longer than INTENT_TICKS, and declaring early is the whole verb);
 * · TAP_ARM: …and it survives the button's RELEASE too, until contact, the ground, or a claim. A tap used
 *   to be client-synthesised into an 18-tick hold, so taps worked only for short flights, invisibly —
 *   one arming truth, sim-side. A SECOND press toggles it off, so declaring is reversible.
 */
function armAerialIntent(state: MatchState, p: PlayerState, buttons: number, pressed: number, cfg: SimConfig): void {
  if (p.sentOff) return;
  const b = state.ball;
  const airborne = simOwnsAerialArm(b, cfg); // D155: one predicate, shared with the client's input buffer
  const sticky = cfg.actions.aerial.TAP_ARM === 1 && airborne;
  const armPress = b.possessor !== p.id ? pressed & KICK_BITS_MASK : 0;
  if (armPress !== 0) {
    // a second press while already armed on the same bit TOGGLES it off (PT4-P11)
    if (sticky && (p.intentBits & armPress & BTN.SHOOT) !== 0) p.intentBits &= ~armPress;
    else {
      p.intentBits |= armPress;
      p.intentTick = state.tick;
    }
    return;
  }
  if (!sticky) p.intentBits &= buttons; // ordinary rule: a released button disarms
  if (airborne && (p.intentBits & BTN.SHOOT) !== 0 && (sticky || (buttons & BTN.SHOOT) !== 0)) {
    p.intentTick = state.tick; // hanging ball ⇒ the declaration does not time out
    return;
  }
  if (state.tick - p.intentTick > cfg.actions.kick.INTENT_TICKS) p.intentBits = 0;
}

/** Which kick button to charge on a rising edge (priority SHOOT > THROUGH > PASS). */
function pickKickButton(pressed: number): number {
  if (pressed & BTN.SHOOT) return BTN.SHOOT;
  if (pressed & BTN.THROUGH) return BTN.THROUGH;
  if (pressed & BTN.PASS) return BTN.PASS;
  return 0;
}

/**
 * Step 2 of §5.1 — the action state machine. M1 scope: sprint hysteresis, IDLE↔RUN, the charged kick
 * (KICK_WINDUP → KICK_RELEASE, §5.3/§5.4), and duration-based recovery transitions (KICK_RELEASE,
 * TACKLE_*, RAGDOLL/GETUP). Slide/tackle bodies and punch/dive/gk fill in across M1/M5. Collects
 * kicks into `kicks` (applied to the ball after integration).
 */
export function applyActions(
  state: MatchState,
  inputs: readonly PlayerInput[],
  cfg: SimConfig,
  kicks: KickRequest[],
  events: SimEvent[] = [],
): void {
  const dur = cfg.actions.DUR;
  // D113/HB-P5: the tick's aerial-strike claim. One ball, one strike; ascending EntityId wins.
  const aerialClaim = { struck: false };
  for (const p of state.players) {
    if (p.sentOff) {
      p.sprinting = false;
      p.shouting = false;
      p.calling = false;
      continue;
    }
    const input = inputs[p.id];
    const buttons = input?.buttons ?? 0;
    const moving = !!input && (input.moveX !== 0 || input.moveY !== 0);
    const pressed = buttons & ~p.prevButtons; // rising edges
    updateSprint(p, buttons, moving, cfg);
    // D135 (§5.2) — ARMING IS INTENT, so it is read for EVERY state, not only IDLE/RUN/SHIELD/BLOCK.
    // The arm press-edge used to live inside that one case, so a press during your own KICK_RELEASE (the
    // six ticks after the cross — the natural moment to declare the finish), or during TRAP, HEADER_JUMP
    // or a tackle, was permanently swallowed while `prevButtons` advanced regardless. Shot→rebound and
    // cross→run-in presses become reliable. D120 is untouched: nothing FIRES from here, consumption
    // stays exactly where it was, so no strike ever comes out of a TACKLE or a RAGDOLL.
    armAerialIntent(state, p, buttons, pressed, cfg);
    p.shouting = (buttons & BTN.SHOUT) !== 0; // SHOUT is a hold: doubles the voice radius (§13.4, T-ACT-016)
    p.calling = (buttons & BTN.CALL) !== 0; // D42: "pass to me" — read by teammates' AI + the pass soft-lock
    // D137: D88's set-piece MANUAL charge is deleted (§7.3, PT4-P2 — "a shot is a shot, holding is just
    // aiming"). A taker charges like everyone else, i.e. not at all: the D49 auto-power applies to him
    // too, his windup caps at CHARGE_CAP, and the overcharge scatter it fed is gone from applyKicks.

    switch (p.action) {
      case 'IDLE':
      case 'RUN':
      case 'SHIELD': // D82: held stances re-evaluate from the same branch (drop on release, one-shots interrupt)
      case 'BLOCK': {
        if (pressed & (BTN.GK_DIVE_L | BTN.GK_DIVE_R) && canGkDive(state, p)) {
          // GK dive: own box only, keeper (or anyone < 3-a-side, P1). L = dive −y, R = +y (§5.4)
          p.action = 'GK_DIVE';
          p.actionTicks = 0;
          p.charge = null;
          const side = pressed & BTN.GK_DIVE_L ? -1 : 1;
          p.vel.x = 0;
          p.vel.y = side * cfg.actions.gk.DIVE_SPEED;
          // D125: aim the dive AT THE BALL, in three dimensions. The dive used to be a fixed sideways
          // shove at DIVE_SPEED with z ≡ 0 — so the keeper skated flat across the line no matter where
          // the ball actually was, which is why no animation could ever "fit the trajectory". Solve the
          // flight to his own x-line: the crossing y sets how hard he goes sideways, and a crossing
          // ABOVE standing reach makes him leave the ground. The z integration already exists
          // (movement.ts) and player z already ships on the snapshot, so this costs nothing on the wire.
          const b = state.ball;
          const speedX = Math.abs(b.vel.x);
          if (speedX > 1e-3) {
            const tt = Math.abs(b.pos.x - p.pos.x) / speedX; // seconds until it crosses him
            const yAt = b.pos.y + b.vel.y * tt;
            const zAt = Math.max(0, b.pos.z + b.vel.z * tt - 0.5 * cfg.ball.GRAVITY * tt * tt);
            // lateral: scale toward the crossing, never beyond the dive's own speed
            const need = (yAt - p.pos.y) / Math.max(tt, 1e-3);
            p.vel.y = Math.max(-cfg.actions.gk.DIVE_SPEED, Math.min(cfg.actions.gk.DIVE_SPEED, need));
            if (p.vel.y === 0) p.vel.y = side * 0.01; // keep the dive's SIDE well-defined for the render
            // vertical: only leave the ground when the ball is genuinely above a standing catch
            const reachZ = cfg.actions.gk.DIVE_HAND_Z;
            if (zAt > reachZ) {
              const needVz = (zAt - reachZ) / Math.max(tt, 1e-3) + 0.5 * cfg.ball.GRAVITY * tt;
              p.vz = Math.min(cfg.actions.gk.DIVE_VZ_MAX, Math.max(0, needVz));
            }
          }
          break;
        }
        if (pressed & BTN.SLIDE) {
          // slide tackle: commit, entry speed = 1.4× current (§5.4)
          p.action = 'TACKLE_SLIDE';
          p.actionTicks = 0;
          p.charge = null;
          p.vel.x *= cfg.actions.slide.ENTRY_MUL;
          p.vel.y *= cfg.actions.slide.ENTRY_MUL;
          break;
        }
        if (pressed & BTN.TACKLE) {
          // D18 contextual keeper verbs: in own box, F = pickup (loose ball) or smother (carried ball)
          if (gkRole(state, p) && inOwnBox(state, p) && state.ball.gkLock !== p.id) {
            if (canGkPickup(state, p, cfg)) {
              gkPickup(state, p, events);
              break;
            }
            const b = state.ball;
            const carrier = b.possessor !== null ? state.players[b.possessor] : undefined;
            if (carrier && carrier.team !== p.team && Math.hypot(b.pos.x - p.pos.x, b.pos.y - p.pos.y) <= cfg.actions.gk.SMOTHER_TRIGGER) {
              p.action = 'GK_SMOTHER';
              p.actionTicks = 0;
              p.charge = null;
              break;
            }
          }
          p.action = 'TACKLE_STAND';
          p.actionTicks = 0;
          p.charge = null;
          if (input) {
            // D40 lunge: a committal step toward the aim — the tackle reaches where you pointed it
            p.vel.x += Math.cos(input.aimYaw) * cfg.actions.tackle.LUNGE_BOOST;
            p.vel.y += Math.sin(input.aimYaw) * cfg.actions.tackle.LUNGE_BOOST;
          }
          break;
        }
        if (pressed & BTN.TRAP) {
          // D45 Item 6: catch/trap (E) — gather a loose ball, or a weak clean-side poke vs a carrier.
          // Never a foul; resolved in resolveTrap during the window (§5.5-adjacent, before the referee).
          p.action = 'TRAP';
          p.actionTicks = 0;
          p.charge = null;
          break;
        }
        if (pressed & BTN.SHOVE) {
          // D40 shoulder barge: momentum carries through the (input-ignored) SHOVE window; resolveBarge
          // judges the side-on contact — landed legal barges stagger the carrier and free the ball
          p.action = 'SHOVE';
          p.actionTicks = 0;
          p.charge = null;
          break;
        }
        if (pressed & BTN.DIVE) {
          // simulate a foul: fall along the player-authored aimYaw (§5.4/§7.4 — aiming the fall is the skill)
          p.action = 'DIVE_FALL';
          p.actionTicks = 0;
          p.charge = null;
          if (input) p.facing = input.aimYaw;
          break;
        }
        if (pressed & BTN.PUNCH) {
          // throw a punch along aimYaw (§5.4): WINDUP → ACTIVE → RECOVERY, rooted throughout. D67: the
          // windup spans the anims_punch swing so the victim is thrown on the fist-contact frame, not at
          // the animation's start (see cfg.actions.DUR.PUNCH_WINDUP).
          p.action = 'PUNCH_WINDUP';
          p.actionTicks = 0;
          p.charge = null;
          p.punchHit = false;
          if (input) p.facing = input.aimYaw;
          break;
        }
        // D53 one-touch intent: a kick button HELD (no fresh edge needed) fires FIRST-TIME when
        // possession just arrived — press pass/shoot while the ball is still travelling to you and the
        // ball is played on the instant it's yours. SHOOT skips its windup here (a volleyed release,
        // full auto power). The window is short (INTENT_TICKS after the claim), so habitually holding
        // a button while dribbling never re-triggers.
        const lt = state.ball.lastTouch;
        const freshClaim =
          state.ball.possessor === p.id && lt !== null && lt.entity === p.id && state.tick - lt.tick <= cfg.actions.kick.INTENT_TICKS;
        // D62's latch (fire only from ARMED intent, never a raw held bit), D113's hanging-ball refresh
        // and the arm press-edge itself all live in `armAerialIntent` now, run once per player above for
        // EVERY state. This case used to keep a verbatim copy of those four rules, and the copy's
        // disarm had no TAP_ARM check — so a declaration made on a ball still outside kick.REACH (the
        // incoming cross PT4-P11 exists for) died one tick after the release, and a toggle-off from
        // IDLE was re-armed on the spot. One arming truth; nothing is re-derived here.
        // D137 (§7.3) — the self-LOB. A one-frame BTN.LOB pulse from the CARRIER knocks his own ball up
        // and forward at the `kick.LOB` rung chosen by whether BTN.LOFT rides along (tap vs double-tap):
        // "single press of the button without an action just lofts the ball up… quick double tap lofts
        // higher". PT4-P20 — the action is deliberately NOT changed: a lob is a touch, not a
        // stop-and-chip, so he keeps moving and steering through the release and the D110 z-physics,
        // aerial finish and knock-on machinery compose on top (lob it over him, run round, volley it).
        // Negative space: without the ball, or on a dead ball the restart rules would refuse, it is inert
        // — never a phantom windup.
        if ((pressed & BTN.LOB) !== 0 && input && state.ball.possessor === p.id && !restartKickRefused(state, p, cfg)) {
          const lob = cfg.actions.kick.LOB;
          const rung = (buttons & BTN.LOFT) !== 0 ? 1 : 0;
          kicks.push({
            entity: p.id,
            team: p.team,
            button: BTN.LOB, // not a kick VERB: no soft-lock, no SHOT, no charge curve
            chargeTicks: cfg.actions.kick.CHARGE_CAP,
            aimYaw: input.aimYaw,
            aimElev: input.aimElev,
            speed: lob.SPEED[rung],
            elev: lob.ELEV[rung],
          });
        }
        // D113: if the ball is in the strike envelope, this tick IS the finish (volley or jump).
        // D155: …or it is the WAIT before it. A declared volley whose contact is still 2–3 ticks out must
        // not fall through into the ordinary kick machinery: the same held SHOOT would open a
        // KICK_WINDUP, and a windup never re-enters this case, so the strike could never happen and the
        // charged shot fired in its place. He holds his stance and finishes on the contact tick.
        const aerial = tryAerialStrike(state, p, input, buttons, cfg, kicks, aerialClaim);
        if (aerial === 'STRUCK') break;
        if (aerial === 'WAITING') {
          p.action = moving ? 'RUN' : 'IDLE';
          break;
        }
        const armed = p.intentBits & buttons;
        // inside the window: an ARMED volley or a genuine fresh press. Only a bit that is merely HELD,
        // never armed and not pressed this tick — i.e. held straight through my own kick — is excluded.
        const kb = pickKickButton(freshClaim ? armed | pressed : pressed);
        // D53: no kick starts without a KICKABLE ball — a press while the ball is still travelling to
        // you used to fire an air-kick (KICK_RELEASE = no-touch, blocking the very claim you wanted) or
        // park you in a phantom windup. The press is swallowed; the held bit + the fresh-claim rule
        // above volley the ball the instant it arrives.
        if (kb !== 0) {
          // D129 (PT4-P19): …and the same for a kick the DEAD-BALL rules will refuse — during the
          // ceremony, or by the defending team. The clip's taker played the whole windup animation on
          // every press while `applyKicks` silently dropped the kick, which is why "I kicked and nothing
          // happened" read as a broken button rather than as a rule. Note this is NOT gated on
          // possession: the kickoff taker already owns the ball, and his ceremony press must be swallowed
          // too. Arming (above) has already happened, so a press held across the whistle still fires.
          if (restartKickRefused(state, p, cfg)) {
            p.action = moving ? 'RUN' : 'IDLE';
            break;
          }
          const bd = Math.hypot(state.ball.pos.x - p.pos.x, state.ball.pos.y - p.pos.y);
          if (state.ball.possessor !== p.id && bd > cfg.actions.kick.REACH) {
            p.action = moving ? 'RUN' : 'IDLE';
            break;
          }
        }
        // D49: PASS/THROUGH are INSTANT — no hold-to-charge; the press IS the release. Power is
        // auto-computed in applyKicks (arrival-calibrated to the always-present receiver), so the
        // chargeTicks here is only the no-teammate fallback. SHOOT keeps its windup (the hold shows
        // the arc; power is auto there too) — except on a one-touch intent (above), where it fires now.
        if (cfg.actions.kick.AUTO_POWER === 1 && (kb === BTN.PASS || kb === BTN.THROUGH || (kb === BTN.SHOOT && freshClaim))) {
          if (input) {
            // D110: the LOFT hold-modifier rides along on the request's button word (BTN.LOFT is not a
            // kick bit, so it never picks a verb — it only decides whether that verb flies).
            // D133/PT4-P9: a keeper's FOOT pass gets the D34 soft-lock like anybody else. D46's blanket
            // `assist: !p.isGk` was designed for GK_HOLD's own-goal geometry and also stripped the lead
            // from every keeper FIELD pass — so his Space pass demanded pixel aim no other player needs,
            // which is exactly half of "goal kick: only shoot works". The hand release below keeps
            // assist:false: that geometry is what D46 actually fixed.
            kicks.push({ entity: p.id, team: p.team, button: kb | (buttons & BTN.LOFT), chargeTicks: Math.round(cfg.actions.kick.CHARGE_CAP * 0.6), aimYaw: input.aimYaw, aimElev: input.aimElev, assist: true });
          }
          p.action = 'KICK_RELEASE';
          p.actionTicks = 0;
          p.charge = null;
          break;
        }
        if (kb !== 0) {
          p.action = 'KICK_WINDUP';
          p.actionTicks = 0;
          p.charge = { button: kb, ticks: 1 };
          break;
        }
        // D82 held stances — X while carrying = SHIELD (the D80 counter-verb), CONTAIN while defending =
        // a braced BLOCK body. Evaluated last so a tackle/kick still interrupts them; re-latched each tick
        // from the held button and dropping to RUN/IDLE on release. Entry (action change) resets actionTicks.
        const isCarrying = state.ball.possessor === p.id;
        if (isCarrying && (buttons & BTN.SHIELD) !== 0) {
          if (p.action !== 'SHIELD') p.actionTicks = 0;
          p.action = 'SHIELD';
          break;
        }
        if (!isCarrying && (buttons & BTN.CONTAIN) !== 0) {
          if (p.action !== 'BLOCK') p.actionTicks = 0;
          p.action = 'BLOCK';
          p.sprinting = false; // a braced block doesn't sprint
          break;
        }
        p.action = moving ? 'RUN' : 'IDLE';
        break;
      }
      case 'KICK_WINDUP': {
        const synthetic = (buttons & BTN.SYNTHETIC) !== 0;
        const stillHeld = p.charge !== null && (buttons & p.charge.button) !== 0;
        if (stillHeld && !synthetic) {
          p.charge!.ticks = Math.min(p.charge!.ticks + 1, cfg.actions.kick.CHARGE_CAP); // D137: one rule for everyone
        } else if (synthetic) {
          // release edge on a SYNTHETIC frame cancels the charge instead of firing (§5.4c)
          p.charge = null;
          p.action = 'IDLE';
          p.actionTicks = 0;
        } else {
          // released → fire. D46: a designated keeper distributing skips the D34 pass soft-lock — the AI
          // already chose a cone-safe aim; letting the soft-lock bend it toward a receiver reopens the
          // own-goal (gkRole is true for everyone < 3-a-side, so gate on isGk to keep small-sided assist).
          if (p.charge && input) {
            // D48: a KEEPER striking his own carried ball repositions it to the release point along the
            // aim first — a short-charge clear (panic = 2 ticks) fires before the facing slew brings the
            // carried ball around, and launching through his own capsule ricocheted it goalward (the
            // same self-collision the GK_HOLD release had).
            if (p.isGk && state.ball.possessor === p.id) {
              const releaseAt = Math.max(cfg.possession.CARRY_POINT, cfg.collision.CAPSULE_R + cfg.ball.RADIUS + 0.05);
              state.ball.pos.x = p.pos.x + Math.cos(input.aimYaw) * releaseAt;
              state.ball.pos.y = p.pos.y + Math.sin(input.aimYaw) * releaseAt;
              state.ball.pos.z = 0;
              p.facing = input.aimYaw;
            }
            kicks.push({ entity: p.id, team: p.team, button: p.charge.button | (buttons & BTN.LOFT), chargeTicks: p.charge.ticks, aimYaw: input.aimYaw, aimElev: input.aimElev, assist: true }); // D133/PT4-P9
          }
          p.charge = null;
          p.action = 'KICK_RELEASE';
          p.actionTicks = 0;
        }
        break;
      }
      case 'KICK_RELEASE':
        if (p.actionTicks >= (dur.KICK_RELEASE ?? 6)) resetToIdle(p);
        break;
      case 'HEADER_JUMP':
        // D113: IP:406's row, finally in the state. The redirect fires on the first tick of the effect
        // window with the ball in the head sphere; missing it costs the airtime and nothing else.
        resolveHeaderContact(state, p, input, cfg, kicks, aerialClaim);
        if (p.actionTicks >= (dur.HEADER_JUMP ?? 14)) {
          p.intentBits &= ~BTN.SHOOT; // a jump consumes the declaration whether or not it connected
          resetToIdle(p);
        }
        break;
      case 'SHOVE':
        if (p.actionTicks >= (dur.SHOVE ?? 8)) resetToIdle(p);
        break;
      case 'TRAP': // D45 Item 6: gather window elapses ⇒ back to idle (a successful catch already set GK-less possession)
        if (p.actionTicks >= (dur.TRAP ?? 8)) resetToIdle(p);
        break;
      case 'CELEBRATE': // D108a: this case was MISSING, so the action never retired and the player was
        // stuck in the celebration animation. DUR.CELEBRATE was configured but nothing read it.
        if (p.actionTicks >= (dur.CELEBRATE ?? 60)) resetToIdle(p);
        break;
      case 'TACKLE_STAND': {
        if (p.actionTicks >= (dur.TACKLE_STAND ?? 10)) {
          // D35 whiff stumble: a standing tackle that didn't win the ball ends in a GETUP stumble —
          // F-spam becomes a read-punishable commitment (bots pay the same price). D39: only when
          // there was a real duel target (an opposing CARRIER in range) — pressing F near a loose
          // ball is a harmless habit, not a faceplant.
          const holder = state.ball.possessor !== null ? state.players[state.ball.possessor] : undefined;
          const dueled =
            holder !== undefined &&
            holder.team !== p.team &&
            Math.hypot(holder.pos.x - p.pos.x, holder.pos.y - p.pos.y) < cfg.actions.tackle.STAND_REACH + 1.2;
          if (cfg.actions.duel.WHIFF_STUMBLE === 1 && state.ball.possessor !== p.id && dueled) {
            p.action = 'GETUP';
            p.actionTicks = Math.floor((dur.GETUP ?? 12) * cfg.actions.duel.WHIFF_STUMBLE_SKIP); // D40: shorter punishment
          } else resetToIdle(p);
        }
        break;
      }
      case 'TACKLE_SLIDE':
        if (p.actionTicks >= (dur.TACKLE_SLIDE ?? 18)) {
          p.action = 'SLIDE_RECOVERY';
          p.actionTicks = 0;
          p.vel.x = 0; // entering SLIDE_RECOVERY zeroes velocity (§5.4)
          p.vel.y = 0;
        }
        break;
      case 'SLIDE_RECOVERY':
        if (p.actionTicks >= (dur.SLIDE_RECOVERY ?? 24)) resetToIdle(p);
        break;
      case 'RAGDOLL':
        if (p.actionTicks >= (dur.RAGDOLL ?? 45)) {
          p.action = 'GETUP';
          p.actionTicks = 0;
        }
        break;
      case 'GETUP':
        if (p.actionTicks >= (dur.GETUP ?? 12)) resetToIdle(p);
        break;
      case 'PRONE':
        if (p.actionTicks >= (dur.PRONE ?? 20)) {
          p.action = 'GETUP';
          p.actionTicks = 0;
        }
        break;
      case 'GK_SMOTHER':
        if (p.actionTicks >= (dur.GK_SMOTHER ?? 8)) {
          p.action = 'PRONE'; // whiffed smother — the goal is open (D18 balance cost)
          p.actionTicks = 0;
        }
        break;
      case 'GK_DIVE':
        if (p.actionTicks >= (dur.GK_DIVE ?? 16)) {
          p.action = 'PRONE'; // dive that didn't catch ends face-down (§5.4)
          p.actionTicks = 0;
        }
        break;
      case 'DIVE_FALL':
        if (p.actionTicks >= (dur.DIVE_FALL ?? 20)) {
          p.action = 'PRONE'; // the simulated fall ends prone (§5.4)
          p.actionTicks = 0;
        }
        break;
      case 'PUNCH_WINDUP':
        if (p.actionTicks >= (dur.PUNCH_WINDUP ?? 24)) {
          p.action = 'PUNCH_ACTIVE';
          p.actionTicks = 0;
        }
        break;
      case 'PUNCH_ACTIVE':
        if (p.actionTicks >= (dur.PUNCH_ACTIVE ?? 3)) {
          p.action = 'PUNCH_RECOVERY';
          p.actionTicks = 0;
        }
        break;
      case 'PUNCH_RECOVERY':
        if (p.actionTicks >= (p.punchHit ? cfg.actions.PUNCH_RECOVERY_HIT : cfg.actions.PUNCH_RECOVERY_WHIFF)) {
          p.punchHit = false;
          resetToIdle(p);
        }
        break;
      case 'GK_HOLD': {
        // D62: he must still HAVE the ball. This case repositions the ball into the keeper's hands
        // unconditionally, and a restart placed EARLIER IN THE SAME TICK (advancePhase is sub-step 1,
        // applyActions sub-step 2 — e.g. the HALFTIME expiry starting the second-half kickoff) had its
        // ball teleported straight back out to the keeper, up to 37 m from the spot. applyKicks then
        // refused his kick under the D38 restart discipline, so the ball just sat there and the match
        // was dead until the restart timed out 10 s later.
        if (state.ball.possessor !== p.id) {
          resetToIdle(p);
          break;
        }
        // D35 catch-settle: the keeper gathers the ball for SETTLE_TICKS — no distribution yet
        if (p.actionTicks < cfg.actions.gk.SETTLE_TICKS) break;
        // distribute on a kick edge (aim + kick only), or auto-release at the hold limit (§5.4/T-POS-007)
        const kb = pickKickButton(pressed);
        const timeUp = p.actionTicks >= (dur.GK_HOLD ?? cfg.ai.GK_HOLD_LIMIT);
        if (kb !== 0 || timeUp) {
          const button = kb !== 0 ? kb : BTN.PASS;
          const aimYaw = input ? input.aimYaw : state.attackDir[p.team] > 0 ? 0 : Math.PI;
          // D44: distance-calibrated so the distribution arrives (was a fixed DISTRIBUTE_CHARGE that
          // under/over-hit); a THROUGH/SHOOT distribution keeps its own harder button, only PASS calibrates.
          const chargeTicks = button === BTN.PASS ? passChargeFor(distributeDistance(state, p, aimYaw), cfg, state.conditions) : cfg.actions.gk.DISTRIBUTE_CHARGE;
          // D46: a GK_HOLD distribution is always a keeper release — skip the pass soft-lock (assist:false)
          // and honour the keeper's chosen loft (input.aimElev) so a lofted punt clears a camped press.
          // D48: release from the HANDS along the aim. Facing froze wherever he gathered the ball (often
          // goal-side), the held ball rode pos + facing·CARRY_POINT, and the kick fired THROUGH his own
          // capsule — the ricochet was the own-goal machine the D46 cone couldn't see. A keeper throwing
          // from hands picks his release point: pivot to the aim and put the ball down out front, just
          // past ball↔body contact reach so the launch can never graze his own capsule.
          const releaseAt = Math.max(cfg.possession.CARRY_POINT, cfg.collision.CAPSULE_R + cfg.ball.RADIUS + 0.05);
          p.facing = aimYaw;
          state.ball.pos.x = p.pos.x + Math.cos(aimYaw) * releaseAt;
          state.ball.pos.y = p.pos.y + Math.sin(aimYaw) * releaseAt;
          state.ball.pos.z = 0;
          kicks.push({ entity: p.id, team: p.team, button, chargeTicks, aimYaw, aimElev: input ? input.aimElev : 0, assist: false });
          state.ball.gkLock = p.id; // D18: no re-handling until another player touches
          p.action = 'KICK_RELEASE';
          p.actionTicks = 0;
          p.charge = null;
        }
        break;
      }
      default:
        // other actions (slide, punch, dive, gk, shield, header, celebrate) land in later slices
        break;
    }
  }
}

function resetToIdle(p: PlayerState): void {
  p.action = 'IDLE';
  p.actionTicks = 0;
}

/**
 * Post-collision ragdoll trigger (§5.4d). Any contact whose total impulse ≥ RAGDOLL_IMPULSE_MIN
 * ragdolls the involved players, cancelling any charge without firing (§5.4b), and emits a RAGDOLL
 * event (cause COLLISION for M1; FOUL/PUNCH causes arrive with the referee slice, M5).
 */
export function applyRagdolls(
  state: MatchState,
  contacts: readonly ContactEvent[],
  cfg: SimConfig,
  events: SimEvent[],
): void {
  const min = cfg.actions.RAGDOLL_IMPULSE_MIN;
  for (const c of contacts) {
    if (c.impulse < min) continue;
    for (const id of [c.a, c.b]) {
      const p = state.players[id];
      if (!p || p.sentOff || p.action === 'RAGDOLL') continue;
      // tackler gating (§5.4d): a ball-first tackle is clean (no self-ragdoll); a no-ball tackle
      // ragdolls the tackler only when RAGDOLL_TACKLER is set. Non-tackle participants ragdoll normally.
      // D121: ask what he was doing AT CONTACT — recorded on the ContactEvent — not what he is doing
      // now. A tackle that connects retires its own window in step 5d, which runs BEFORE this, so
      // reading the live action would strip a clean tackler of his own exemption and ragdoll him.
      const actionAtContact = id === c.a ? c.aAction : c.bAction;
      if (actionAtContact === 'TACKLE_STAND' || actionAtContact === 'TACKLE_SLIDE') {
        if (c.ballFirst === true) continue;
        if (cfg.actions.RAGDOLL_TACKLER !== 1) continue;
      }
      p.action = 'RAGDOLL';
      p.actionTicks = 0;
      p.charge = null; // §5.4b: involuntary transition cancels the charge without firing
      events.push({ t: 'RAGDOLL', entity: id, cause: 'COLLISION' });
    }
  }
}
